import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { UserAccountTicketsConfigurationRequest } from "@/api/types";
import imgPlaceholder from "@/assets/images/image-placeholder.png";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { isDocumentUploaded } from "@/components/DocumentInput/useDocumentInput";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormDocumentInput } from "@/components/Form/FormDocumentInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { AnnotationAlertIcon } from "@/components/Icons/AnnotationAlertIcon";
import { Building01Icon } from "@/components/Icons/Building01Icon";
import { ChevronLeftIcon } from "@/components/Icons/ChevronLeftIcon";
import { Image01Icon } from "@/components/Icons/Image01Icon";
import { MessageCircle01Icon } from "@/components/Icons/MessageCircle01Icon";
import { Send03Icon } from "@/components/Icons/Send03Icon";
import { WithAppPreviewLayout } from "@/components/Layouts/WithAppPreviewLayout";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Phone } from "@/components/Phone/Phone";
import { PhoneInner } from "@/components/Phone/PhoneInner";
import { PhoneStatusBar } from "@/components/Phone/PhoneStatusBar";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { Badge } from "@/design-system/Badge";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { ContentBlock } from "@/design-system/ContentBlock";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { Switch } from "@/design-system/Switch";
import { Tiles } from "@/design-system/Tiles";
import { ALLOWED_FILE_TYPES } from "@/helpers/file-types";
import { createDocumentSizeRule, createDocumentTypeRule, createRequiredStringRule } from "@/helpers/rules";
import { isValidHttpUrl } from "@/helpers/validation";
import { useBool } from "@/hooks/useBool";
import { fileMutations } from "@/queries/files/mutations";
import { portfolioTicketsMutations } from "@/queries/portfolio-tickets/mutations";
import { usePortfolioTicketsQueries } from "@/queries/portfolio-tickets/queryOptions";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

interface FormValues {
  customIntroText?: string;
  regulationDocument?: FormDocument[];
  regulationLink?: string;
  requireSignOffRegulations: boolean;
  autoReplyText?: string;
}
interface PreviewData {
  showIntro: boolean;
  customIntroText: string;
  showRegulations: boolean;
  isRegulationsRequired: boolean;
  showAutoReply: boolean;
  autoReplyText: string;
}
const MAX_CUSTOM_INTRO_TEXT_LENGTH = 400;
const MAX_AUTO_REPLY_TEXT_LENGTH = 1200;

export function TicketSettingsPage(): React.ReactNode {
  const { t } = useTranslation();
  const portfolioTicketsQueries = usePortfolioTicketsQueries();
  const setTicketSettings = portfolioTicketsMutations.usePortfolioTicketUpdateSettings();
  const [previewData, setPreviewData] = useState<PreviewData>({
    showIntro: false,
    customIntroText: "",
    showRegulations: false,
    isRegulationsRequired: false,
    showAutoReply: false,
    autoReplyText: "",
  });
  const {
    data: ticketSettings,
    isPending: isLoadingTicketSettings,
    error: errorLoadingTicketSettings,
  } = useQuery(portfolioTicketsQueries.userProfileTicketsConfiguration());
  const { mutateAsync: uploadDocument, isPending: isUploadingDocument } = fileMutations.useAddDocument();

  async function uploadDocumentIfNotUploaded(values: FormValues): Promise<string | undefined> {
    const document = values.regulationDocument?.[0];
    const link = values.regulationLink?.trim();
    if (isDocumentUploaded(document)) {
      return document.id;
    } else if (document) {
      const uploadedDocument = await uploadDocument({
        file: document.file,
      });

      return uploadedDocument.id;
    } else if (link) {
      if (link === ticketSettings?.regulations?.url) {
        return ticketSettings.regulations.id;
      } else {
        const uploadedDocument = await uploadDocument({ link });

        return uploadedDocument.id;
      }
    } else {
      return undefined;
    }
  }
  async function handleSubmit(values: FormValues) {
    const regulationsId = await uploadDocumentIfNotUploaded(values);
    await setTicketSettings.mutateAsync({
      customIntroText: values.customIntroText ? values.customIntroText : undefined,
      regulationsId: regulationsId ?? undefined,
      requireSignOffRegulations: values.requireSignOffRegulations,
      autoReplyText: values.autoReplyText ? values.autoReplyText : undefined,
    });
  }
  if (isLoadingTicketSettings) {
    return <FullSizeLoader />;
  }
  if (errorLoadingTicketSettings) {
    return <ErrorPage error={errorLoadingTicketSettings} />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.ticket-settings.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.ticket-settings.breadcrumbs.tickets"),
              to: routes.portfolio.tickets(),
            },
            {
              name: t("page.ticket-settings.title"),
            },
          ]}
        />
      }
    >
      <WithAppPreviewLayout
        preview={
          <>
            {previewData.showIntro ? (
              <TicketIntroTextAppPreview
                customIntroText={previewData.customIntroText}
                showRegulations={previewData.showRegulations}
                isRegulationsRequired={previewData.isRegulationsRequired}
              />
            ) : null}
            {previewData.showAutoReply ? <TicketAutoReplyAppPreview autoReplyText={previewData.autoReplyText} /> : null}
          </>
        }
        hidePreview={!previewData.showIntro && !previewData.showAutoReply}
      >
        <ContentBlock width="constrained">
          <SectionCard
            title={t("page.ticket-settings.custom-settings.title")}
            description={t("page.ticket-settings.custom-settings.subtext")}
          >
            <TicketSettingsForm
              defaultFormValues={{
                customIntroText: ticketSettings?.customIntroText,
                requireSignOffRegulations: ticketSettings?.requireSignOffRegulations ?? false,
                regulationDocument: ticketSettings.regulations?.fileName ? [ticketSettings.regulations] : undefined,
                regulationLink:
                  ticketSettings?.regulations?.url && !ticketSettings?.regulations?.fileName
                    ? ticketSettings.regulations.url
                    : undefined,
                autoReplyText: ticketSettings?.autoReplyText,
              }}
              onSubmit={handleSubmit}
              isSubmitting={setTicketSettings.isPending || isUploadingDocument}
              onChange={setPreviewData}
            />
          </SectionCard>
        </ContentBlock>
      </WithAppPreviewLayout>
    </DocumentPage>
  );
}
interface TicketSettingsFormProps {
  defaultFormValues: FormValues;
  onSubmit: (values: UserAccountTicketsConfigurationRequest) => void;
  isSubmitting: boolean;
  onChange: (data: PreviewData) => void;
}
function TicketSettingsForm({
  defaultFormValues,
  onSubmit,
  isSubmitting,
  onChange,
}: TicketSettingsFormProps): React.ReactNode {
  const form = useForm<FormValues>({
    defaultValues: defaultFormValues,
  });
  const { t } = useTranslation();
  const [showCustomIntroText, showCustomIntroTextHandler] = useBool(!!defaultFormValues.customIntroText);
  const [showAutoReplyText, showAutoReplyTextHandler] = useBool(!!defaultFormValues.autoReplyText);
  const customIntroText = useWatch({
    control: form.control,
    name: "customIntroText",
  });
  const requireSignOffRegulations = useWatch({
    control: form.control,
    name: "requireSignOffRegulations",
  });
  const regulationDocument = useWatch({
    control: form.control,
    name: "regulationDocument",
  });
  const regulationLink = useWatch({
    control: form.control,
    name: "regulationLink",
  });
  const autoReplyText = useWatch({
    control: form.control,
    name: "autoReplyText",
  });
  useEffect(() => {
    onChange({
      showIntro: showCustomIntroText,
      customIntroText: customIntroText ?? "",
      showRegulations: requireSignOffRegulations || !!regulationDocument || !!regulationLink,
      isRegulationsRequired: requireSignOffRegulations,
      showAutoReply: showAutoReplyText,
      autoReplyText: autoReplyText ?? "",
    });
  }, [
    onChange,
    showCustomIntroText,
    customIntroText,
    requireSignOffRegulations,
    regulationDocument,
    regulationLink,
    showAutoReplyText,
    autoReplyText,
  ]);
  useEffect(() => {
    if (!showCustomIntroText) {
      form.setValue("customIntroText", "");
      form.setValue("requireSignOffRegulations", false);
      form.setValue("regulationDocument", undefined);
      form.setValue("regulationLink", "");
    }
    if (showCustomIntroText && !form.getValues("customIntroText")) {
      form.setValue("customIntroText", t("page.resident-tickets.create-modal.intro-text"));
    }
    if (!showAutoReplyText) {
      form.setValue("autoReplyText", "");
    }
  }, [t, showCustomIntroText, showAutoReplyText, form]);

  return (
    <Form.Root formMethods={form} onSubmit={onSubmit}>
      <Form.Body>
        <FormField
          label={t("page.ticket-settings.ticket-conditions.title")}
          description={t("page.ticket-settings.ticket-conditions.subtext")}
        >
          <Switch
            isChecked={showCustomIntroText}
            onChange={showCustomIntroTextHandler.toggle}
            ariaLabel={t("page.ticket-settings.ticket-conditions.title")}
          />
        </FormField>
        {showCustomIntroText ? (
          <>
            <FormField label={t("page.ticket-settings.ticket-conditions.label")} required>
              <FormTextArea<FormValues>
                name="customIntroText"
                placeholder={t("page.ticket-settings.ticket-conditions.placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.ticket-settings.ticket-conditions.label"),
                  },
                  maxLength: {
                    value: MAX_CUSTOM_INTRO_TEXT_LENGTH,
                    message: t("components.form.error.max-length", {
                      length: MAX_CUSTOM_INTRO_TEXT_LENGTH,
                    }),
                  },
                }}
              />
            </FormField>
            <FormCheckbox<FormValues>
              name="requireSignOffRegulations"
              label={t("page.ticket-settings.ticket-regulations.title")}
            />
            <FormField
              label={t("page.ticket-settings.ticket-regulations.file.label")}
              required={requireSignOffRegulations}
            >
              <FormDocumentInput<FormValues, "regulationDocument">
                name="regulationDocument"
                accept={ALLOWED_FILE_TYPES.DOCUMENT}
                disabled={!!regulationLink}
                rules={{
                  validate: {
                    required: () => {
                      if (requireSignOffRegulations && !regulationLink && !regulationDocument?.[0]) {
                        return t("components.form.error.either-required", {
                          inputName1: t("page.ticket-settings.ticket-regulations.file.label"),
                          inputName2: t("page.ticket-settings.ticket-regulations.url.label"),
                        });
                      }
                    },
                    size: createDocumentSizeRule({
                      t,
                    }),
                    type: createDocumentTypeRule({
                      t,
                    }),
                  },
                }}
              />
            </FormField>

            <span className="text-body-emphasis text-text-secondary uppercase">
              {t("page.ticket-settings.ticket-regulations.separator")}
            </span>

            <FormField
              label={t("page.ticket-settings.ticket-regulations.url.label")}
              required={requireSignOffRegulations}
            >
              <FormInput<FormValues, "regulationLink">
                name="regulationLink"
                placeholder={t("page.ticket-settings.ticket-regulations.url.placeholder")}
                disabled={regulationDocument && regulationDocument.length > 0}
                rules={{
                  validate: {
                    required: () => {
                      if (requireSignOffRegulations && !regulationLink && !regulationDocument?.[0]) {
                        return t("components.form.error.either-required", {
                          inputName1: t("page.ticket-settings.ticket-regulations.file.label"),
                          inputName2: t("page.ticket-settings.ticket-regulations.url.label"),
                        });
                      }
                    },
                    isValid: (value) => {
                      if (value && !isValidHttpUrl(value.trim())) {
                        return t("components.form.error.invalid-url");
                      }
                    },
                  },
                }}
              />
            </FormField>
          </>
        ) : null}
        <FormField
          label={t("page.ticket-settings.auto-reply.title")}
          description={t("page.ticket-settings.auto-reply.subtext")}
        >
          <Switch
            isChecked={showAutoReplyText}
            onChange={showAutoReplyTextHandler.toggle}
            ariaLabel={t("page.ticket-settings.auto-reply.title")}
          />
        </FormField>
        {showAutoReplyText ? (
          <FormField label={t("page.ticket-settings.auto-reply.label")} required>
            <FormTextArea<FormValues>
              name="autoReplyText"
              placeholder={t("page.ticket-settings.auto-reply.placeholder")}
              rules={{
                validate: {
                  required: createRequiredStringRule(t, "page.ticket-settings.auto-reply.label"),
                },
                maxLength: {
                  value: MAX_AUTO_REPLY_TEXT_LENGTH,
                  message: t("components.form.error.max-length", {
                    length: MAX_AUTO_REPLY_TEXT_LENGTH,
                  }),
                },
              }}
            />
          </FormField>
        ) : null}
      </Form.Body>
      <Form.Footer
        renderPrimaryAction={() => (
          <Button type="submit" isLoading={isSubmitting}>
            {t("page.ticket-settings.submit")}
          </Button>
        )}
      />
    </Form.Root>
  );
}
interface TicketIntroTextAppPreviewProps {
  customIntroText: string;
  showRegulations: boolean;
  isRegulationsRequired: boolean;
}
function TicketIntroTextAppPreview({
  customIntroText,
  showRegulations,
  isRegulationsRequired,
}: TicketIntroTextAppPreviewProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <div className="size-full">
      <Phone>
        <div className="size-full bg-aop-off-white">
          <PhoneStatusBar fill="black" />
        </div>
        <article className="absolute top-control-md left-1/2 size-full -translate-x-1/2 rounded-lg bg-aop-off-white p-space-sm pt-0">
          <header className="grid grid-cols-3 p-space-md">
            <ChevronLeftIcon />
            <span className="w-fit justify-self-center text-overline-emphasis text-text-brand-secondary">
              {t("page.ticket-settings.app-preview.intro-text.title")}
            </span>
          </header>
          <section className="flex w-full flex-col gap-space-md px-space-md">
            <article className="flex flex-col gap-space-md rounded-md bg-background-default p-space-md pt-space-sm">
              <Stack gap="xs">
                <span className="text-body-emphasis">{t("page.ticket-settings.app-preview.intro-text.subject")}</span>
                <span className="text-overline">{t("page.ticket-settings.app-preview.intro-text.description")}</span>
              </Stack>
              <Tiles columns={3} gap="xs">
                {new Array(3).fill(null).map((_, index) => (
                  <div key={index} className="aspect-square size-full overflow-hidden rounded-xs">
                    <img src={imgPlaceholder} className="size-full object-cover" alt="App preview ticket attachment" />
                  </div>
                ))}
              </Tiles>
              <dl className="flex w-full flex-col gap-space-xs">
                <dt className="origin-top-left text-overline-emphasis text-text-secondary">
                  {t("page.ticket-settings.app-preview.intro-text.visibility")}
                </dt>
                <dd className="flex w-full items-center gap-space-xs">
                  <Building01Icon size={12} />
                  <span className="w-full origin-top-left text-overline">
                    {t("page.ticket-settings.app-preview.intro-text.visibility.building")}
                  </span>
                </dd>
              </dl>
            </article>
            {customIntroText.trim() && (
              <Stack gap="xs">
                <span className="text-body-emphasis">
                  {t("page.ticket-settings.app-preview.intro-text.intro-text.title")}
                </span>
                <span className="**:text-overline">
                  <MultiLineContent>{customIntroText}</MultiLineContent>
                </span>
              </Stack>
            )}
            <Stack gap="xs">
              {showRegulations && (
                <aside className="flex items-center gap-space-sm rounded-md bg-background-default p-space-md">
                  <span className="flex-1 text-overline">
                    <Trans
                      i18nKey="page.ticket-settings.app-preview.intro-text.regulations"
                      components={{
                        b: <span className="text-overline-emphasis text-text-brand" />,
                      }}
                    />
                  </span>
                  {isRegulationsRequired && (
                    <div className="origin-right scale-50">
                      <Switch onChange={() => {}} isChecked={false} ariaLabel="" />
                    </div>
                  )}
                </aside>
              )}
            </Stack>
            <footer className="rounded-lg bg-background-brand p-space-md text-center text-text-default-inverse">
              {t("common.action.send")}
            </footer>
          </section>
        </article>
      </Phone>
    </div>
  );
}
interface TicketAutoReplyAppPreviewProps {
  autoReplyText: string;
}
function TicketAutoReplyAppPreview({ autoReplyText }: TicketAutoReplyAppPreviewProps): React.ReactNode {
  const [previewDate] = useState(() => new Date());
  const { i18n, t } = useTranslation();

  return (
    <article className="flex w-full max-w-measure-compact flex-col items-center gap-space-md">
      <span className="text-body-emphasis">{t("page.ticket-settings.app-preview.title.auto-reply")}</span>
      <div className="size-full">
        <Phone>
          <PhoneInner noBackground>
            <section className="relative flex size-full flex-col items-center bg-aop-off-white">
              <span className="mt-control-md text-overline-emphasis text-text-brand-secondary">
                {t("page.ticket-settings.app-preview.auto-reply.title")}
              </span>
              <section className="mt-space-md flex min-h-0 w-full flex-1 flex-col overflow-y-auto">
                <aside className="flex items-center gap-space-md bg-background-brand-light p-space-md text-text-info">
                  <AnnotationAlertIcon />
                  <span className="text-text-primary">
                    {t("page.ticket-settings.app-preview.auto-reply.visibility")}
                  </span>
                </aside>
                <Box padding="md" background="default">
                  <Stack gap="md">
                    <Badge variant="warning">{t("page.ticket-settings.app-preview.auto-reply.status")}</Badge>
                    <Inline gap="sm" align="center" wrap={false}>
                      <UserAvatar image={undefined} />
                      <span className="flex w-full flex-col gap-space-xs text-text-secondary">
                        <span>
                          <Trans
                            i18nKey="page.ticket-settings.app-preview.auto-reply.reporter"
                            components={{
                              b: <strong className="text-text-primary" />,
                            }}
                          />
                        </span>
                        <span>
                          {t("page.ticket-settings.app-preview.auto-reply.date", {
                            date: formatDate(i18n, "datetime", previewDate),
                          })}
                        </span>
                      </span>
                    </Inline>
                    <span className="text-caption-emphasis">
                      {t("page.ticket-settings.app-preview.auto-reply.ticket-title")}
                    </span>
                    <span className="text-overline">
                      {t("page.ticket-settings.app-preview.auto-reply.ticket-description")}
                    </span>
                    <Inline gap="xl" align="center" wrap={false}>
                      <span className="flex items-center gap-space-xs text-overline-emphasis">
                        <MessageCircle01Icon /> 0
                      </span>
                      <span className="text-overline-emphasis text-text-brand">
                        {t("page.ticket-settings.app-preview.auto-reply.close")}
                      </span>
                    </Inline>
                  </Stack>
                </Box>
                <article className="flex items-start gap-space-sm p-space-md">
                  <UserAvatar image={undefined} />
                  <section className="flex w-full flex-col gap-space-xs rounded-lg bg-background-default p-space-sm">
                    <span className="text-overline-emphasis">
                      {t("page.ticket-settings.app-preview.auto-reply.account")}
                    </span>
                    <span className="text-overline whitespace-pre-line">{autoReplyText}</span>
                  </section>
                </article>
              </section>
            </section>
            <footer className="absolute bottom-0 left-0 w-full bg-aop-off-white p-space-md">
              <Inline gap="sm" wrap={false}>
                <div className="size-fit rounded-xs bg-background-default p-space-xs">
                  <Image01Icon size={20} />
                </div>
                <span className="flex w-full items-center justify-between rounded-xs bg-background-default p-space-xs">
                  <span className="text-overline text-text-secondary">
                    {t("page.ticket-settings.app-preview.auto-reply.comment")}
                  </span>
                  <span className="size-fit rounded-xs bg-background-brand p-space-xs text-text-default-inverse">
                    <Send03Icon />
                  </span>
                </span>
              </Inline>
            </footer>
          </PhoneInner>
        </Phone>
      </div>
    </article>
  );
}
